import { useState } from 'react';
import { GraduationCap, Mail, ArrowUpRight, ArrowRight } from 'lucide-react';
import {
  BG, ACCENT, ACCENT_BRIGHT, FONT_DISPLAY, FONT_BODY, FONT_MONO,
  TEXT, HAIRLINE, glow, microLabel, primaryButton, accentOutlineButton,
} from '@/styles/premium';

interface GraduationNoticeProps {
  userName?: string;
  /** Semester label from the academic calendar, e.g. "Spring 2026". */
  semesterLabel: string;
  onDismiss: () => void;
}

export default function GraduationNotice({ userName, semesterLabel, onDismiss }: GraduationNoticeProps) {
  return (
    <div
      style={{
        background: BG,
        fontFamily: FONT_BODY,
        color: TEXT.primary,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(16px,5vw,48px)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient blooms, matching the hero treatment on About */}
      <div style={{ ...glow(420, 0.1), top: -140, right: -120 }} />
      <div style={{ ...glow(360, 0.07), bottom: -160, left: -140 }} />

      {/* No entrance animation: this card replaces the whole page, so it must
          never depend on an animation frame to become visible. */}
      <div style={{ width: '100%', maxWidth: 560, position: 'relative', zIndex: 1 }}>
        <div
          style={{
            background: 'linear-gradient(135deg,rgba(20,184,166,.12) 0%,rgba(6,182,212,.07) 100%)',
            border: '1px solid rgba(20,184,166,.18)',
            borderRadius: 20,
            padding: 'clamp(24px,5vw,40px)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ ...glow(200, 0.16), top: -60, right: -40 }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Micro-label + icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'clamp(20px,4vw,28px)' }}>
              <div
                style={{
                  width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                  background: 'rgba(20,184,166,.15)',
                  border: '1px solid rgba(20,184,166,.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <GraduationCap size={20} color={ACCENT} aria-hidden />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...microLabel, marginBottom: 3 }}>Semester VIII · Complete</div>
                <div style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '.1em', color: TEXT.ghost }}>
                  {semesterLabel.toUpperCase()}
                </div>
              </div>
            </div>

            {/* Headline */}
            <h1
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: 'clamp(30px,6.5vw,46px)',
                fontWeight: 400,
                letterSpacing: '-.03em',
                lineHeight: 1.02,
                marginBottom: 16,
              }}
            >
              {userName ? (
                <>Congratulations,<br />
                  <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.35)' }}>{userName}</em>
                </>
              ) : (
                <>You made it to<br />
                  <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,.35)' }}>the finish line</em>
                </>
              )}
            </h1>

            {/* Good luck copy */}
            <p
              style={{
                fontSize: 'clamp(13px,1.6vw,14px)',
                lineHeight: 1.75,
                color: TEXT.muted,
                fontWeight: 300,
                marginBottom: 12,
                maxWidth: 440,
              }}
            >
              You've reached the end of <span style={{ color: 'rgba(255,255,255,.7)' }}>{semesterLabel}</span> —
              the last stretch of your degree. That's no small thing.
            </p>
            <p
              style={{
                fontSize: 'clamp(13px,1.6vw,14px)',
                lineHeight: 1.75,
                color: TEXT.muted,
                fontWeight: 300,
                marginBottom: 'clamp(24px,4vw,32px)',
                maxWidth: 440,
              }}
            >
              Best of luck with whatever comes next — a job, a startup, or more study.
              Go build something worth talking about.
            </p>

            {/* Promo */}
            <div style={{ borderTop: `1px solid ${HAIRLINE}`, paddingTop: 'clamp(20px,4vw,26px)' }}>
              <div style={{ ...microLabel, marginBottom: 10 }}>Available for work</div>
              <p
                style={{
                  fontSize: 'clamp(12px,1.5vw,13px)',
                  lineHeight: 1.7,
                  color: TEXT.faint,
                  fontWeight: 300,
                  marginBottom: 18,
                  maxWidth: 440,
                }}
              >
                Need a developer for what's next? I build production ML systems, web apps and AI
                products — the same stack behind UniSchedule.
              </p>

              <div className="flex flex-col sm:flex-row" style={{ gap: 10 }}>
                <PromoLink href="https://hakimi.tech" label="hakimi.tech" external />
                <PromoLink href="mailto:info@unischedule.me?subject=Project%20enquiry" label="info@unischedule.me" />
              </div>
            </div>

            {/* Dismiss */}
            <ContinueButton onClick={onDismiss} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PromoLink({ href, label, external }: { href: string; label: string; external?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...accentOutlineButton,
        flex: 1,
        minWidth: 0,
        background: hov ? 'rgba(20,184,166,.1)' : 'transparent',
        borderColor: hov ? 'rgba(20,184,166,.7)' : 'rgba(20,184,166,.5)',
      }}
    >
      {!external && <Mail size={13} aria-hidden />}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {external && <ArrowUpRight size={13} aria-hidden />}
    </a>
  );
}

function ContinueButton({ onClick }: { onClick: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        ...primaryButton,
        marginTop: 'clamp(20px,4vw,26px)',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        background: hov ? ACCENT_BRIGHT : ACCENT,
        borderColor: hov ? ACCENT_BRIGHT : ACCENT,
      }}
    >
      Continue to app <ArrowRight size={15} aria-hidden />
    </button>
  );
}
